/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import styled from 'styled-components';
import { List, Loader } from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import { Table, EllipsisLoader } from '../../../components';
import { FormattedMessage } from 'react-intl';
import ProductDetailsButton from './ProductDetailsButton';
import EditProductButton from './EditProductButton';
import DeleteProductButton from './DeleteProductButton';
import InfiniteScroll from 'react-infinite-scroller';
import type { Product, ProductEdge, PageInfo } from '../../../type';

// Query for list of products of current salon
export const GET_PRODUCTS_QUERY = gql`
    query getProducts(
        $salon: ID!
        $search: String
        $manufacturersFilter: [String]
        $first: Int
        $after: String
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    products(
                        search: $search
                        manufacturersFilter: $manufacturersFilter
                        first: $first
                        after: $after
                    ) {
                        pageInfo {
                            endCursor
                            hasNextPage
                        }
                        edges {
                            cursor
                            node {
                                id
                                ...product
                            }
                        }
                    }
                }
            }
        }
    }

    fragment product on Product {
        name
        code
        manufacturer {
            id
            name
        }
        quantity
        price
        supplierPrice
        type
    }
`;

type ProductsListProps = {
    products: Array<ProductEdge>,
    loadMore: Function,
    pageInfo: PageInfo,
};

type ProductRowProps = {
    product: Product,
};

const LoadMoreSpinner = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={6} textAlign="center">
            <EllipsisLoader size="1em" />
        </Table.Cell>
    </Table.Row>
);
const NameCell = styled(Table.Cell)`padding: 0px !important;`;

/**
 * Show this message if inventory list is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={6} textAlign="center">
            <FormattedMessage
                id="pages.inventory.ProductList.noProduct"
                defaultMessage={"You don't have products yet"}
            />
        </Table.Cell>
    </Table.Row>
);

const ProductRow = ({ product }: ProductRowProps) => (
    <Table.Row>
        <NameCell>
            <ProductDetailsButton product={product}>
                {product.name}
            </ProductDetailsButton>
        </NameCell>
        <Table.Cell>{product.manufacturer.name}</Table.Cell>
        <Table.Cell>{product.code}</Table.Cell>
        <Table.Cell textAlign="right">{product.quantity}</Table.Cell>
        <Table.Cell textAlign="right">
            {parseFloat(product.price).toFixed(2)} €
        </Table.Cell>
        <Table.Cell>
            <List horizontal divided>
                <List.Item>
                    <EditProductButton product={product} />
                </List.Item>
                <List.Item>
                    <DeleteProductButton product={product} />
                </List.Item>
            </List>
        </Table.Cell>
    </Table.Row>
);

const ProductsListView = ({ products, pageInfo, loadMore }: ProductsListProps) => (
    <Table celled selectable striped fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.name"
                        defaultMessage="Name"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.manufacturer"
                        defaultMessage="Manufacturer"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.productCode"
                        defaultMessage="Product Code"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.inventory.ProductList.qty"
                        defaultMessage="Quantity"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.inventory.ProductList.price"
                        defaultMessage="Price"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.actions"
                        defaultMessage="Actions"
                    />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <InfiniteScroll
            element="tbody"
            pageStart={0}
            loadMore={loadMore}
            initialLoad={false}
            hasMore={pageInfo.hasNextPage}
            loader={<LoadMoreSpinner key={0} />}
            threshold={1}
            useWindow
        >
            {isEmpty(products) ? (
                <EmptyMessage />
            ) : (
                    products.map(product => (
                        <ProductRow key={product.cursor} product={product.node} />
                    ))
                )}
        </InfiniteScroll>
    </Table>
);

function ProductsList() {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ inventory }) => inventory.get('searchQuery'));
    const manufacturersFilter = useSelector(({ inventory }) => inventory.get('manufacturersFilter'));

    const { loading, data, fetchMore } = useQuery(GET_PRODUCTS_QUERY, {
        variables: {
            salon,
            search: searchQuery || undefined,
            manufacturersFilter: manufacturersFilter || undefined,
            first: 500,
        },
        fetchPolicy: 'cache-and-network',
    });

    const products = get(data, 'viewer.salon.products.edges', []);
    const pageInfo = get(data, 'viewer.salon.products.pageInfo', {});

    /**
     * Handle load more results event by requesting data after
     * end cursor from the last request.
     */
    const loadMore = () => {
        // Skip if we already loading results or that was the last page
        if (loading || !pageInfo.hasNextPage) {
            return false;
        }

        fetchMore({
            query: GET_PRODUCTS_QUERY,
            variables: {
                salon,
                first: 500,
                after: pageInfo.endCursor,
                search: searchQuery || undefined,
                manufacturersFilter: manufacturersFilter || undefined,
            },
            // Merge current results with a new results
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const prevEdges = previousResult.viewer.salon.products.edges;
                const newEdges = fetchMoreResult.viewer.salon.products.edges;

                const newResults = Object.assign({}, fetchMoreResult);
                newResults.viewer.salon.products.edges = [
                    ...prevEdges,
                    ...newEdges,
                ];

                return newResults;
            },
        });
    };

    if (loading) {
        return <Loader size="big" inline="centered" active />;
    }

    return (
        <ProductsListView
            products={products}
            pageInfo={pageInfo}
            loadMore={loadMore}
        />
    );
}

export default ProductsList;
